import dayjs from "dayjs";
import {
  InventoryDocumentType,
  ItemSkuQtysQuery,
  Tracability,
} from "../../../generated/inventory";
import { IUser } from "../../../types/Auth/user";
import { ITraceEntry } from "../../../types/Inventory";
import { v4 as uuidv4 } from "uuid";
import { IGoodsIssue } from "../../../types/Inventory/GoodsIssue";
import { IApprovalTemplate, IReviewedBy } from "../../../types/global";

export const goodsIssueCreatePayloadFormatter = (
  data: IGoodsIssue,
  status: string,
  approval_step?: number
) => {
  const {
    created_date,
    main_status,
    aggrid_status,
    trace_entry_list,
    warehouse_id,
    ...otherPayload
  } = data;
  let allTraceEntries: ITraceEntry[] = [];

  trace_entry_list.forEach((trace) => {
    if (trace.serial_list) {
      if (trace.serial_list.length > 0) {
        const formatSerialList = trace.serial_list.map(
          ({
            uom,
            source_bin_location,
            destination_bin_location,
            all_bin_locations,
            stock_qty,
            ...serial
          }) => ({
            ...serial,
            qty:
              typeof serial.qty === "string"
                ? parseInt(serial.qty)
                : serial.qty,
            reference_unique_id: data.unique_id,
            reference_line_item: trace.reference_line_item,
            document_item_qty: trace.document_item_qty,
            posted_qty: trace.posted_qty,
            // uom_unique_id: uom?.unique_id,
          })
        );
        allTraceEntries.push(...formatSerialList);
      } else {
        const {
          serial_list,
          uom,
          source_bin_location,
          destination_bin_location,
          all_bin_locations,
          stock_qty,
          ...otherTrace
        } = trace;
        const formatTrace = {
          ...otherTrace,
          reference_unique_id: data.unique_id,
          qty:
            typeof otherTrace.qty === "string"
              ? parseInt(otherTrace.qty)
              : otherTrace.qty,
          // uom_unique_id: uom?.unique_id,
        };
        allTraceEntries.push(formatTrace);
      }
    } else {
      allTraceEntries.push(trace);
    }
  });

  const payload = {
    ...otherPayload,
    trace_entry_list: allTraceEntries,
    main_status: status,
    warehouse_id:
      typeof warehouse_id === "string" ? parseInt(warehouse_id) : warehouse_id,
    approval_step: approval_step ? 1 : 0,
  };
  return payload;
};

const groupBy = (xs: any[], key: string) => {
  return xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

export const goodsIssueQueryFormatter = (
  data: IGoodsIssue,
  allSkuQtys?: ItemSkuQtysQuery
) => {
  const { trace_entry_list, created_date, posted_date, ...otherData } = data;
  const groupedTrace = groupBy(trace_entry_list, "item_unique_id");

  const allTraceEntries: ITraceEntry[] = [];
  for (const entry of Object.entries(groupedTrace)) {
    const key = entry[0] as string;
    const value = entry[1] as ITraceEntry[];

    const foundAllSku =
      allSkuQtys && allSkuQtys.itemSkuQtys && allSkuQtys.itemSkuQtys.length > 0
        ? allSkuQtys.itemSkuQtys.filter(
            (sku) => sku?.barcode === value[0].barcode
          )
        : [];

    const allBinLocation = foundAllSku.map((sku) => ({
      id: sku?.bin_id,
      name: sku?.bin_name,
      stock_qty: sku?.stock_qty,
      warehouse_id: sku?.warehouse_id,
    }));

    const filterBinLocations = allBinLocation.filter(
      (bin) => bin.warehouse_id === otherData.warehouse_id
    );

    const foundBinLocation = filterBinLocations.find(
      (bin) => bin.id === value[0].source_bin_location_id
    );

    const formatSerial = value.map((serial) => ({
      ...serial,
      posted_date: serial.posted_date ?? undefined,
      scanned_by: serial.scanned_by ?? undefined,
      all_bin_locations: filterBinLocations,
      stock_qty: foundBinLocation?.stock_qty,
    }));

    const formatTrace: ITraceEntry = {
      item_unique_id: key,
      is_stockable: value[0].is_stockable,
      sku_name: value[0].sku_name,
      item_name: value[0].item_name,
      item_img_url: value[0].item_img_url,
      tracability: value[0].tracability,
      type: value[0].type,
      unique_id: value[0].unique_id,
      qty: value.reduce((prev, curr) => prev + curr.qty, 0),
      posted_qty: value[0].posted_qty,
      document_item_qty: value[0].document_item_qty,
      posted_date:
        value[0].tracability === Tracability.Normal
          ? value[0].posted_date
          : undefined,
      created_date: value[0].created_date,
      uom: value[0].uom,
      source_bin_location:
        value[0].tracability === Tracability.Normal
          ? value[0].source_bin_location
          : undefined,
      source_bin_location_id:
        value[0].tracability === Tracability.Normal
          ? value[0].source_bin_location_id
          : undefined,
      barcode:
        value[0].tracability === Tracability.Normal
          ? value[0].barcode
          : undefined,
      serial_list: value.some(
        (serial) => serial.tracability === Tracability.Normal
      )
        ? undefined
        : formatSerial,
      status:
        value[0].tracability === Tracability.Normal
          ? value[0].status
          : undefined,
      scanned_by:
        value[0].tracability === Tracability.Normal
          ? value[0].scanned_by ?? undefined
          : undefined,
      all_bin_locations: filterBinLocations,
      reference_unique_id: value[0].reference_unique_id,
      reference_line_item: value[0].reference_line_item,
      stock_qty:
        value[0].tracability === Tracability.Normal
          ? foundBinLocation?.stock_qty
          : formatSerial?.reduce<number>(
              (prev, curr) => prev + (curr.stock_qty ?? 0),
              0
            ),
      remark: value[0].remark,
    };

    allTraceEntries.push(formatTrace);
  }

  return {
    ...otherData,
    trace_entry_list: allTraceEntries,
    created_date: created_date ? dayjs(created_date) : undefined,
    posted_date: posted_date ? dayjs(posted_date) : undefined,
  };
};

export const goodsIssueTraceEntryFormatter = (
  trace: ITraceEntry,
  binLocations: any[],
  user: IUser | null
) => {
  const {
    type,
    posted_date,
    created_date,
    scanned_by,
    unique_id,
    ...otherPayload
  } = trace;

  if (user) {
    const formatTrace: ITraceEntry = {
      ...otherPayload,
      qty: 0,
      source_bin_location: binLocations[0],
      source_bin_location_id: binLocations[0].id,
      all_bin_locations: binLocations,
      unique_id: uuidv4(),
      type: InventoryDocumentType.GoodsIssue,
      scanned_by: {
        user_unique_id: user.unique_id,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        img_url: user.img_url,
      },
      posted_date: dayjs().toDate(),
      stock_qty: binLocations[0].stock_qty,
    };
    return formatTrace;
  }
  return;
};

export const goodsIssueUpdatePayloadFormatter = (
  data: IGoodsIssue,
  status: string,
  approval_step?: number,
  approver_list?: IApprovalTemplate[],
  isNotApprove?: boolean,
  reviewed_by?: IReviewedBy
) => {
  const {
    id,
    created_date,
    unique_id,
    main_status,
    flag_status,
    aggrid_status,
    warehouse,
    warehouse_id,
    trace_entry_list,
    created_by,
    branch,
    reviewer_list,
    ...otherPayload
  } = data;
  let allTraceEntries: ITraceEntry[] = [];

  trace_entry_list.forEach((trace) => {
    if (trace.serial_list) {
      if (trace.serial_list.length > 0) {
        const formatSerialList = trace.serial_list.map(
          ({
            uom,
            created_date,
            source_bin_location,
            destination_bin_location,
            all_bin_locations,
            stock_qty,
            ...serial
          }) => ({
            ...serial,
            qty:
              typeof serial.qty === "string"
                ? parseInt(serial.qty)
                : serial.qty,
            reference_unique_id: data.unique_id,
            document_item_qty: trace.document_item_qty,
            posted_qty: trace.posted_qty,
            status: "is_scanned",
          })
        );
        allTraceEntries.push(...formatSerialList);
      } else {
        const {
          serial_list,
          uom,
          source_bin_location,
          destination_bin_location,
          created_date,
          all_bin_locations,
          stock_qty,
          ...otherTrace
        } = trace;
        const formatTrace = {
          ...otherTrace,
          qty:
            typeof otherTrace.qty === "string"
              ? parseInt(otherTrace.qty)
              : otherTrace.qty,
          status: "is_scanned",
          reference_unique_id: data.unique_id,
        };
        allTraceEntries.push(formatTrace);
      }
    } else {
      allTraceEntries.push({
        ...trace,
        status: "is_scanned",
      });
    }
  });

  const payload = {
    ...otherPayload,
    trace_entry_list: allTraceEntries,
    main_status: status,
    warehouse_id:
      typeof warehouse_id === "string" ? parseInt(warehouse_id) : warehouse_id,
    approval_step: approval_step ? approval_step : 0,
    approver_list:
      approver_list && approver_list.length > 0
        ? approver_list
        : data.approver_list,
    flag_status: isNotApprove
      ? [...flag_status.filter((fl) => fl !== "not_approved"), "not_approved"]
      : flag_status.filter((fl) => fl !== "not_approved"),
    reviewer_list: reviewer_list
      ? data.aggrid_status === "wait_approve" && reviewed_by
        ? !isNotApprove
          ? [...reviewer_list, reviewed_by]
          : [...reviewer_list]
        : data.aggrid_status === "not_approved" && status === "wait_approve"
        ? []
        : [...reviewer_list]
      : [],
  };
  return payload;
};

export const goodsIssueTypeValueFormatter = (type: string) => {
  switch (type) {
    case "sales_order":
      return "ขาย";
    case "purchase_return":
      return "ส่งคืน";
    case "other":
      return "อื่นๆ";
    default:
      return "";
  }
};
